// A run on the map (docs/spec/transport.md slice 9), the same everywhere: the way driven as a solid
// line, the planned way as a dashed one under it, stops done (greyed, ticked), next (marigold) or to
// come (numbered), the school, and the bus pointing where it is going.
import { useMemo } from "react";
import { MapView, type MapLine, type MapMarker } from "@vavr/screens/shared";

export interface RunMapStop { key: string; name: string; lat: number | null; lng: number | null; school: boolean; n?: number; state?: "done" | "next" | "todo" }
export interface RunMapBus { lat: number; lng: number; heading?: number | null; label?: string; tone?: MapMarker["tone"]; title?: string }

export const DRIVEN = "#0b564e";
export const PLANNED = "#2e9c8e";

export function runMarkers(stops: RunMapStop[], bus?: RunMapBus | null, busId = "bus"): MapMarker[] {
  return [
    ...stops.filter((s) => s.lat != null && s.lng != null).map((s): MapMarker => (s.school
      ? { id: `school-${s.key}`, lat: s.lat!, lng: s.lng!, kind: "school", title: s.name }
      : { id: `stop-${s.key}`, lat: s.lat!, lng: s.lng!, kind: "stop", label: s.n != null ? String(s.n) : "•", title: `${s.n != null ? `${s.n}. ` : ""}${s.name}${s.state === "done" ? ", done" : s.state === "next" ? ", next" : ""}`, tone: s.state === "done" ? "done" : s.state === "next" ? "next" : undefined })),
    ...(bus ? [{ id: busId, lat: bus.lat, lng: bus.lng, kind: "bus" as const, label: bus.label, heading: bus.heading ?? null, tone: bus.tone, title: bus.title ?? "The bus" }] : []),
  ];
}

export function runLines(id: string, plan: [number, number][] | null | undefined, trail: [number, number][]): MapLine[] {
  return [
    ...(plan && plan.length > 1 ? [{ id: `${id}-plan`, coords: plan, color: PLANNED, width: 4, opacity: 0.75, dash: true }] : []),
    ...(trail.length > 1 ? [{ id: `${id}-trail`, coords: trail, color: DRIVEN, width: 6, opacity: 0.95 }] : []),
  ];
}

export function RunMap({ id, plan, trail, stops, bus, height = 320, label, follow, inset }: {
  id: string; plan?: [number, number][] | null; trail: [number, number][]; stops: RunMapStop[]; bus?: RunMapBus | null; height?: number | string; label: string; follow?: boolean;
  /** Pixels a panel covers at the bottom (or the top): the bus stays in the clear part. */
  inset?: { top?: number; bottom?: number };
}) {
  const lines = useMemo(() => runLines(id, plan, trail), [id, plan, trail]);
  const markers = runMarkers(stops, bus);
  return <MapView label={label} height={height} markers={markers} lines={lines} follow={follow && bus ? ["bus"] : undefined} inset={inset} />;
}

/** The trail as the map wants it. */
export const lngLat = (pts: { lat: number; lng: number }[]) => pts.map((p) => [p.lng, p.lat] as [number, number]);
